import {useMemo, useState} from 'react';

import {Box, ButtonBase, Dialog, DialogContent, InputBase, Stack, Typography} from '@mui/material';

import {Spinner} from '../../contentGenerator/components/ui';
import {C} from '../constants';
import {SectionLabel} from './shared';

const dialogPaperSx = {
    background: C.surface,
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
};

const formatConfigDate = (value = '') => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
};

export default function ReviewConfigsDialog({
    configs = [],
    eligibleGroups = [],
    error = '',
    loading = false,
    onClose,
    onDelete,
    onLoad,
    onSave,
    open,
    saving = false,
}) {
    const [name, setName] = useState('');
    const [localError, setLocalError] = useState('');
    const trimmedName = name.trim();
    const matchingConfig = useMemo(
        () =>
            configs.find(
                (config) => config.name.trim().toLowerCase() === trimmedName.toLowerCase(),
            ) || null,
        [configs, trimmedName],
    );
    const eligibleListingCount = eligibleGroups.reduce(
        (count, group) => count + group.listings.length,
        0,
    );
    const canSave = Boolean(trimmedName && eligibleGroups.length && !saving);

    const handleSave = async () => {
        if (!canSave) {
            setLocalError(
                eligibleGroups.length
                    ? 'Name this configuration before saving.'
                    : 'Create a group with at least two SKUs before saving.',
            );
            return;
        }

        setLocalError('');
        try {
            await onSave(trimmedName, matchingConfig?.id || '');
            setName('');
        } catch (err) {
            setLocalError(err.message || 'Could not save this configuration.');
        }
    };

    // Save the current selection over an existing config by reusing its
    // name + id — skips the name-input step from the top form.
    const handleOverride = async (config) => {
        if (!eligibleGroups.length) {
            setLocalError('Create a group with at least two SKUs before overriding.');
            return;
        }
        if (saving) return;

        setLocalError('');
        try {
            await onSave(config.name, config.id);
        } catch (err) {
            setLocalError(err.message || 'Could not override this configuration.');
        }
    };

    const canOverride = Boolean(eligibleGroups.length && !saving);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{sx: dialogPaperSx}}>
            <Box sx={{padding: '16px', borderBottom: `1px solid ${C.border}`}}>
                <Stack direction="row" alignItems="center" gap="8px">
                    <SectionLabel sx={{color: C.purple, flex: 1}}>Review configs</SectionLabel>
                    <ButtonBase
                        onClick={onClose}
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: C.textSoft,
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: 1,
                        }}>
                        ×
                    </ButtonBase>
                </Stack>
                <Typography sx={{fontSize: 12, color: C.textSoft, marginTop: '6px'}}>
                    Save grouped review content and load it later against the current catalog.
                </Typography>
            </Box>

            <DialogContent sx={{padding: '16px', display: 'grid', gap: '16px'}}>
                <Box
                    sx={{
                        border: `1px solid ${eligibleGroups.length ? `${C.teal}33` : C.border}`,
                        background: eligibleGroups.length ? C.tealL : C.surfaceAlt,
                        borderRadius: '12px',
                        padding: '12px',
                    }}>
                    <SectionLabel
                        sx={{
                            color: eligibleGroups.length ? C.teal : C.textSoft,
                            marginBottom: '8px',
                        }}>
                        Save current groups
                    </SectionLabel>
                    <Typography sx={{fontSize: 12, color: C.textSoft, marginBottom: '10px'}}>
                        {eligibleGroups.length
                            ? `${eligibleGroups.length} group(s), ${eligibleListingCount} SKU(s) eligible.`
                            : 'No eligible groups. A saved configuration needs at least one group with 2 or more SKUs.'}
                    </Typography>
                    <Stack direction={{xs: 'column', sm: 'row'}} gap="8px">
                        <InputBase
                            value={name}
                            onChange={(event) => {
                                setName(event.target.value);
                                setLocalError('');
                            }}
                            placeholder="Configuration name"
                            sx={{
                                flex: 1,
                                width: '100%',
                                boxSizing: 'border-box',
                                fontFamily: 'inherit',
                                fontSize: 13,
                                padding: '8px 10px',
                                borderRadius: '10px',
                                border: `1px solid ${C.border2}`,
                                background: C.surface,
                            }}
                        />
                        <ButtonBase
                            onClick={handleSave}
                            disabled={!canSave}
                            sx={{
                                border: 'none',
                                background: canSave ? C.ink : C.surfaceAlt,
                                color: canSave ? C.surface : C.textSoft,
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontFamily: 'inherit',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: canSave ? 'pointer' : 'default',
                                minWidth: 118,
                            }}>
                            {saving ? (
                                <Stack
                                    component="span"
                                    direction="row"
                                    alignItems="center"
                                    gap="8px">
                                    <Spinner />
                                    Saving
                                </Stack>
                            ) : matchingConfig ? (
                                'Overwrite'
                            ) : (
                                'Save'
                            )}
                        </ButtonBase>
                    </Stack>
                </Box>

                {(localError || error) && (
                    <Box
                        sx={{
                            border: `1px solid ${C.coral}33`,
                            background: C.coralL,
                            color: C.coral,
                            borderRadius: '10px',
                            padding: '8px 10px',
                            fontSize: 11,
                        }}>
                        {localError || error}
                    </Box>
                )}

                <Box>
                    <SectionLabel sx={{marginBottom: '8px'}}>Saved configs</SectionLabel>
                    {loading ? (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap="8px"
                            sx={{fontSize: 12, color: C.textSoft}}>
                            <Spinner />
                            Loading saved configs
                        </Stack>
                    ) : configs.length ? (
                        <Box sx={{display: 'grid', gap: '8px'}}>
                            {configs.map((config) => (
                                <Box
                                    key={config.id}
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        background: C.surface,
                                        borderRadius: '10px',
                                        padding: '10px 12px',
                                    }}>
                                    <Stack direction="row" alignItems="center" gap="10px">
                                        <Box sx={{flex: 1, minWidth: 0}}>
                                            <Typography
                                                sx={{fontSize: 13, fontWeight: 700, color: C.ink}}>
                                                {config.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: C.textSoft,
                                                    marginTop: '2px',
                                                }}>
                                                {config.counts.groups} group(s) ·{' '}
                                                {config.counts.listings} SKU(s)
                                                {formatConfigDate(config.updatedAt)
                                                    ? ` · ${formatConfigDate(config.updatedAt)}`
                                                    : ''}
                                            </Typography>
                                        </Box>
                                        <ButtonBase
                                            onClick={() => onLoad(config)}
                                            disabled={saving}
                                            sx={{
                                                border: `1px solid ${C.teal}44`,
                                                background: C.tealL,
                                                color: C.teal,
                                                borderRadius: '8px',
                                                padding: '6px 10px',
                                                fontFamily: 'inherit',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: saving ? 'default' : 'pointer',
                                            }}>
                                            Load
                                        </ButtonBase>
                                        <ButtonBase
                                            onClick={() => handleOverride(config)}
                                            disabled={!canOverride}
                                            title={
                                                eligibleGroups.length
                                                    ? `Save the current selection over "${config.name}"`
                                                    : 'Create a group with at least two SKUs to override'
                                            }
                                            sx={{
                                                border: `1px solid ${C.amber}44`,
                                                background: canOverride
                                                    ? C.amberL
                                                    : 'transparent',
                                                color: canOverride
                                                    ? C.amber
                                                    : C.textFaint,
                                                borderRadius: '8px',
                                                padding: '6px 10px',
                                                fontFamily: 'inherit',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: canOverride ? 'pointer' : 'default',
                                                opacity: canOverride ? 1 : 0.6,
                                            }}>
                                            Override
                                        </ButtonBase>
                                        <ButtonBase
                                            onClick={() => onDelete(config)}
                                            disabled={saving}
                                            sx={{
                                                border: `1px solid ${C.border}`,
                                                background: 'transparent',
                                                color: C.textSoft,
                                                borderRadius: '8px',
                                                padding: '6px 10px',
                                                fontFamily: 'inherit',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: saving ? 'default' : 'pointer',
                                            }}>
                                            Delete
                                        </ButtonBase>
                                    </Stack>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{fontSize: 12, color: C.textSoft}}>
                            No saved configurations yet.
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{padding: '16px', borderTop: `1px solid ${C.border}`}}>
                <ButtonBase
                    onClick={onClose}
                    sx={{
                        border: 'none',
                        background: C.ink,
                        color: C.surface,
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}>
                    Done
                </ButtonBase>
            </Stack>
        </Dialog>
    );
}
